import React, { useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type {
  OpportunityRelatedBundledExperience,
  OpportunityRelatedDeal,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import getErrorMessage from '~/utils/getErrorMessage';

import TabPanelShell from '../../../shared/TabPanelShell';
import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import SalesforceLinkButton from '../../shared/SalesforceLinkButton';
import {
  formatDealOptionCurrency,
  formatDealOptionValue,
  normaliseDealOptionText as normalise,
} from '../dealOptions/dealOptionsFormatters';
import { handleExpandRowKeyDown, toggleExpandedRow } from '../surcharges/surchargeTabLayout';

const COLUMN_COUNT = 9;

interface Props {
  bundledExperiences: Array<OpportunityRelatedBundledExperience>;
  deals: Array<OpportunityRelatedDeal>;
  error?: unknown;
  isLoading: boolean;
}

interface BundledExperienceGroup {
  dealOptionIds: Array<string>;
  key: string;
  representative: OpportunityRelatedBundledExperience;
}

// Salesforce returns one row per (experience, deal option) pair — group by experience id to dedupe.
function groupByExperience(bundledExperiences: Array<OpportunityRelatedBundledExperience>) {
  const groups = new Map<string, BundledExperienceGroup>();

  bundledExperiences.forEach((bundledExperience) => {
    const key = normalise(bundledExperience.ancillary_deal_option__c) ?? bundledExperience.id;
    const dealOptionId = normalise(bundledExperience.deal_option__c);
    const group = groups.get(key);

    if (group) {
      if (dealOptionId && !group.dealOptionIds.includes(dealOptionId)) {
        group.dealOptionIds.push(dealOptionId);
      }
      return;
    }

    groups.set(key, {
      dealOptionIds: dealOptionId ? [dealOptionId] : [],
      key,
      representative: bundledExperience,
    });
  });

  return Array.from(groups.values());
}

function getDealOptionsSummary(dealOptionNames: Array<string>) {
  if (dealOptionNames.length === 0) {
    return '—';
  }
  if (dealOptionNames.length === 1) {
    return dealOptionNames[0];
  }
  return `${dealOptionNames.length} deal options`;
}

export default function BundledExperiencesTab({ bundledExperiences, deals, error, isLoading }: Props) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const dealNamesById = useMemo(() => {
    const namesById = new Map<string, string>();

    deals.forEach((deal) => {
      const dealId = normalise(deal.sfid) ?? normalise(deal.id !== undefined ? String(deal.id) : undefined);
      const dealName = normalise(deal.name);

      if (dealId && dealName) {
        namesById.set(dealId, dealName);
      }
    });

    return namesById;
  }, [deals]);

  const groups = useMemo(() => groupByExperience(bundledExperiences), [bundledExperiences]);

  useDealInformationTabCount('bundled-experiences', isLoading || error ? undefined : groups.length);

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading bundled experiences.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">{getErrorMessage(error, 'Unable to load Salesforce bundled experiences.')}</Alert>;
  }

  return (
    <TabPanelShell
      title="Bundled Experiences"
      subtitle={`${groups.length} bundled experience${groups.length === 1 ? '' : 's'}`}
      contentSx={{ p: 0 }}
    >
      {groups.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 3 }} variant="body2">
          No bundled experiences are linked to this opportunity.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Listed In</TableCell>
              <TableCell>Account Name</TableCell>
              <TableCell>Deal Options</TableCell>
              <TableCell>Fare Type</TableCell>
              <TableCell>Sub Category</TableCell>
              <TableCell align="right">Ancillary Sell price uplift</TableCell>
              <TableCell align="right">Value (cost currency)</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Destination</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              const bundledExperience = group.representative;
              const currency = bundledExperience.dealoption__currencyisocode ?? '';
              const value = bundledExperience.dealoption__value_cost_currency ?? null;
              const ancillarySellPriceUplift =
                bundledExperience.sell_price_uplift_number__c ?? bundledExperience.sell_price_uplift__c ?? null;
              const accountName = bundledExperience.dealoption__account_name;
              const subCategory = bundledExperience.dealoption__deal_sub_category;
              const destination = bundledExperience.dealoption__destination;
              const isExpanded = expandedRows.has(group.key);
              const dealOptionNames = group.dealOptionIds.map((dealOptionId) => dealNamesById.get(dealOptionId) ?? '—');

              return (
                <React.Fragment key={group.key}>
                  <TableRow>
                    <TableCell>
                      <SalesforceLinkButton value={bundledExperience.view_in_led__c} />
                    </TableCell>
                    <TableCell>{formatDealOptionValue(accountName)}</TableCell>
                    <TableCell>
                      <Stack
                        alignItems="center"
                        direction="row"
                        role="button"
                        spacing={0.5}
                        sx={{ cursor: dealOptionNames.length > 0 ? 'pointer' : 'default' }}
                        tabIndex={dealOptionNames.length > 0 ? 0 : undefined}
                        onClick={() => {
                          if (dealOptionNames.length > 0) {
                            setExpandedRows((current) => toggleExpandedRow(current, group.key));
                          }
                        }}
                        onKeyDown={(event) => {
                          if (dealOptionNames.length > 0) {
                            handleExpandRowKeyDown(event, () =>
                              setExpandedRows((current) => toggleExpandedRow(current, group.key)),
                            );
                          }
                        }}
                      >
                        <Typography variant="body2">{getDealOptionsSummary(dealOptionNames)}</Typography>
                        {dealOptionNames.length > 1 &&
                          (isExpanded ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {formatDealOptionValue(
                        bundledExperience.fare_type_display__c ?? bundledExperience.channel_fare_type__c,
                      )}
                    </TableCell>
                    <TableCell>{formatDealOptionValue(subCategory)}</TableCell>
                    <TableCell align="right">{formatDealOptionValue(ancillarySellPriceUplift)}</TableCell>
                    <TableCell align="right">{formatDealOptionCurrency(currency, value)}</TableCell>
                    <TableCell>{formatDealOptionValue(currency)}</TableCell>
                    <TableCell>{formatDealOptionValue(destination)}</TableCell>
                  </TableRow>
                  {dealOptionNames.length > 1 && (
                    <TableRow>
                      <TableCell sx={{ bgcolor: isExpanded ? 'action.hover' : undefined, p: 0 }} colSpan={COLUMN_COUNT}>
                        <Collapse unmountOnExit in={isExpanded} sx={{ width: '100%' }} timeout="auto">
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 3, py: 1.5 }}>
                            {dealOptionNames.map((dealOptionName, index) => (
                              <Chip
                                key={`${group.key}-${group.dealOptionIds[index]}`}
                                label={dealOptionName}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </TabPanelShell>
  );
}
